import type { PaymentResponse, ThreeDSAuthResult, SDKConfig } from '../types';
import { SDKError } from '../types';
import { ThreeDSModal } from '../utils/modal';

/**
 * Window interface extension for active SDK instance tracking
 */
declare global {
  interface Window {
    __3DS_SDK_ACTIVE_INSTANCE__?: ThreeDSSDK;
  }
}

/**
 * Core 3DS SDK class
 */
export class ThreeDSSDK {
  private config: Required<Pick<SDKConfig, 'timeout' | 'debug'>> & Partial<SDKConfig>;
  private modal: ThreeDSModal | null = null;
  private instanceId: string;

  constructor(config: SDKConfig = {}) {
    this.config = {
      timeout: config.timeout || 900000, // 5 minutes default
      debug: config.debug || false,
      ...config,
    };
    // Generate unique instance ID
    this.instanceId = `sdk-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Logs a debug message if debug mode is enabled
   */
  private log(message: string, ...args: unknown[]): void {
    if (this.config.debug) {
      // eslint-disable-next-line no-console
      console.log(`[3DS-SDK] ${message}`, ...args);
    }
  }

  /**
   * Cleans up any existing active SDK instance
   */
  private cleanupPreviousInstance(): void {
    if (typeof window === 'undefined') return;

    const previousInstance = window.__3DS_SDK_ACTIVE_INSTANCE__;
    if (previousInstance && previousInstance !== this) {
      this.log(`Cleaning up previous SDK instance: ${previousInstance.instanceId}`);
      previousInstance.cleanup();
    }
  }

  /**
   * Sets this instance as the active one
   */
  private setAsActiveInstance(): void {
    if (typeof window === 'undefined') return;
    window.__3DS_SDK_ACTIVE_INSTANCE__ = this;
    this.log(`Set as active SDK instance: ${this.instanceId}`);
  }

  /**
   * Clears this instance as active if it's currently active
   */
  private clearActiveInstance(): void {
    if (typeof window === 'undefined') return;
    if (window.__3DS_SDK_ACTIVE_INSTANCE__ === this) {
      delete window.__3DS_SDK_ACTIVE_INSTANCE__;
      this.log(`Cleared active SDK instance: ${this.instanceId}`);
    }
  }

  /**
   * Cleans up this SDK instance
   */
  private cleanup(): void {
    this.log(`Cleaning up SDK instance: ${this.instanceId}`);
    this.clearActiveInstance();
    if (this.modal) {
      this.modal.close({ success: false, error: 'Replaced by new authentication flow' });
      this.modal = null;
    }
  }

  /**
   * Performs 3DS authentication using backend payment response
   * Opens a modal with iframe that handles the 3DS flow
   * Supports two flows:
   * 1. Redirect flow: uses redirect.url, redirect.method, redirect.data
   * 2. Action flow: uses action and value.payLoad (submits form)
   * 3. Completed transaction: if response has status codes but no redirect/action, processes directly
   */
  async authenticate(paymentResponse: PaymentResponse): Promise<ThreeDSAuthResult> {
    this.log('Starting 3DS authentication', paymentResponse);

    // Check if this is a completed transaction response (has status codes but no redirect/action)
    const hasStatusInfo =
      !!paymentResponse &&
      typeof paymentResponse === 'object' &&
      (paymentResponse.status != null ||
        paymentResponse.statusCode != null ||
        paymentResponse.responseCode != null ||
        paymentResponse.respCode != null ||
        paymentResponse.OriginalTxnStatus != null ||
        paymentResponse.OriginalTxnStatusCode != null);

    const hasNoFlow =
      !!paymentResponse &&
      typeof paymentResponse === 'object' &&
      !paymentResponse.redirect &&
      (!paymentResponse.action || !paymentResponse.value);

    // If it's a completed transaction response, process it directly without modal
    if (hasStatusInfo && hasNoFlow) {
      this.log('Detected completed transaction response, processing without modal', paymentResponse);

      // Determine success based on status codes
      const status = paymentResponse.status || paymentResponse.OriginalTxnStatus || '';
      const statusCode = paymentResponse.statusCode || paymentResponse.OriginalTxnStatusCode;
      const responseCode = paymentResponse.responseCode || paymentResponse.respCode;

      const isSuccess =
        status === 'Successful' ||
        statusCode === 200 ||
        statusCode === 201 ||
        responseCode === 200 ||
        responseCode === 201;

      const result: ThreeDSAuthResult = {
        success: isSuccess,
        error: isSuccess ? undefined : paymentResponse.description || paymentResponse.respMsg,
        data: paymentResponse as Record<string, unknown>,
      };

      this.log('Completed transaction result', result);

      // Call appropriate callback
      if (isSuccess) {
        this.config.onComplete?.(result);
      } else {
        const error = new SDKError(
          result.error || 'Transaction failed',
          'TRANSACTION_FAILED',
          paymentResponse
        );
        this.config.onError?.(error);
      }

      return result;
    }

    // Detect which flow to use with additional type safety
    const isActionFlow =
      !!paymentResponse &&
      typeof paymentResponse === 'object' &&
      paymentResponse.action != null &&
      paymentResponse.value != null &&
      typeof paymentResponse.value === 'object' &&
      paymentResponse.value !== null &&
      'payLoad' in paymentResponse.value &&
      paymentResponse.value.payLoad != null;

    const isRedirectFlow =
      !!paymentResponse &&
      typeof paymentResponse === 'object' &&
      paymentResponse.redirect != null &&
      typeof paymentResponse.redirect === 'object' &&
      typeof paymentResponse.redirect.url === 'string' &&
      !!paymentResponse.redirect.url &&
      typeof paymentResponse.redirect.method === 'string' &&
      !!paymentResponse.redirect.method &&
      paymentResponse.redirect.data != null;

    // Validate payment response - must have either redirect flow or action flow

    if (!isActionFlow && !isRedirectFlow) {
      const error = new SDKError(
        'Invalid payment response: must have either (redirect.url, redirect.method, redirect.data) or (action, value.payLoad)',
        'INVALID_PAYMENT_RESPONSE'
      );
      this.log('Invalid payment response', error);
      this.config.onError?.(error);
      throw error;
    }

    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      const error = new SDKError('SDK must be used in a browser environment', 'BROWSER_REQUIRED');
      this.log('Not in browser environment', error);
      this.config.onError?.(error);
      throw error;
    }

    // Clean up any previous active instance before starting new flow
    this.cleanupPreviousInstance();

    // Set this instance as active
    this.setAsActiveInstance();

    try {
      this.config.onStart?.();

      // Create modal instance
      this.modal = new ThreeDSModal({
        timeout: this.config.timeout,
        debug: this.config.debug,
        modalWidth: this.config.modalWidth,
        modalHeight: this.config.modalHeight,
        modalContainer: this.config.modalContainer,
        customClass: this.config.customClass,
        onComplete: (result): void => {
          this.log('Authentication completed', result);
          this.config.onComplete?.(result);
        },
        onError: (error): void => {
          this.log('Authentication error', error);
          this.config.onError?.(error);
        },
      });

      // Open modal and wait for result
      const result = await this.modal.open(paymentResponse);
      this.log('Authentication result', result);

      return result;
    } catch (error) {
      this.log('Authentication failed', error);

      const sdkError =
        error instanceof SDKError
          ? error
          : new SDKError(
              error instanceof Error ? error.message : 'Unknown error occurred',
              'AUTH_FAILED'
            );

      this.config.onError?.(sdkError);
      throw sdkError;
    } finally {
      // Clean up modal reference and clear active instance
      this.modal = null;
      this.clearActiveInstance();
    }
  }

  /**
   * Manually close the authentication modal
   */
  closeModal(): void {
    if (this.modal) {
      this.modal.close({ success: false, error: 'Manually closed' });
      this.modal = null;
    }
    this.clearActiveInstance();
  }

  /**
   * Updates SDK configuration
   */
  updateConfig(config: Partial<SDKConfig>): void {
    this.config = {
      ...this.config,
      ...config,
    };
    this.log('Configuration updated', this.config);
  }

  /**
   * Gets current SDK configuration
   */
  getConfig(): Readonly<SDKConfig> {
    return { ...this.config };
  }
}
